import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import api from 'api';
import type { visaApplicationSteps } from 'constant';
import { setAlert } from 'redux/alert';
import type {
    CompletedFormInterface,
    PurposeInfo,
    SpouseInfo,
    AdditionalDetailsForm,
    PreviouslyHeldVisa,
    FamilyMember,
    LanguageProficiency,
    Education,
    WorkExperience,
    PartnerWorkExperienceInterface,
    PartnerEducationInterface,
    JobOffer,
    RelativeAbroad,
    BusinessOwnership,
    PersonInfoForm,
    CitizenshipInfo,
    TicketInterface,
} from 'types';
import { useSpouseUserId } from 'hooks/useSpouseUserId';
import { getActingProfileUserId } from 'utils/selectedAccount';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { mergeSpouseUserTables, splitSpouseProfilePayload } from 'utils/spouseProfilePayload';

const STALE_5_MIN = 5 * 60 * 1000;
const STALE_1_HOUR = 60 * 60 * 1000;

const LOAD_ERROR_FALLBACK = 'Failed to load data. Please try again.';
const SAVE_ERROR_FALLBACK = 'Failed to save data. Please try again.';

/** Surface query failures via global alert (React Query v5 has no useQuery onError). */
function useAlertOnQueryError(error: unknown, fallback = LOAD_ERROR_FALLBACK) {
    const dispatch = useDispatch();

    useEffect(() => {
        if (!error) return;
        dispatch(
            setAlert({
                message: getErrorMessage(error, fallback),
                type: 'error',
            })
        );
    }, [error, dispatch, fallback]);
}

/** Shared mutation onError — step components should not re-alert in catch. */
function useMutationErrorAlert(fallback = SAVE_ERROR_FALLBACK) {
    const dispatch = useDispatch();

    return useCallback(
        (error: unknown) => {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, fallback),
                    type: 'error',
                })
            );
        },
        [dispatch, fallback]
    );
}

/* ═══════════════════════════════════════════════════════
   Query keys
   ═══════════════════════════════════════════════════════ */

export const visaApplicationKeys = {
    personalInfo: ['personalInfo'] as const,
    purposeInfo: ['purposeInfo'] as const,
    citizenshipInfo: ['citizenshipInfo'] as const,
    additionalDetails: ['additionalDetails'] as const,
    previousVisaDetails: ['previousVisaDetails'] as const,
    spouseInfo: ['spouseInfo'] as const,
    familyMembers: ['familyMembers'] as const,
    children: ['children'] as const,
    languageProficiency: ['languageProficiency'] as const,
    spouseLanguageProficiency: ['spouseLanguageProficiency'] as const,
    education: ['education'] as const,
    workExperience: ['workExperience'] as const,
    spouseWorkExperience: ['spouseWorkExperience'] as const,
    jobOffer: ['jobOffer'] as const,
    relativesAbroad: ['relativesAbroad'] as const,
    businessOwnership: ['businessOwnership'] as const,
    completedForms: (userId?: number) => ['completedForms', userId] as const,
    applicationTickets: (applicationId: number | null) =>
        ['applicationTickets', applicationId] as const,
};

/* ═══════════════════════════════════════════════════════
   Completed Forms
   ═══════════════════════════════════════════════════════ */

export function useCompletedForms(userId: number) {
    const profileId = getActingProfileUserId() ?? userId;
    const query = useQuery<CompletedFormInterface | null>({
        queryKey: visaApplicationKeys.completedForms(profileId),
        queryFn: async () => {
            if (!profileId || profileId <= 0) return null;
            const { data } = await api.visaApplication.getCompletedForms(profileId);
            return (data as CompletedFormInterface) ?? null;
        },
        enabled: Number.isFinite(profileId) && profileId > 0,
        staleTime: STALE_5_MIN,
        // Avoid extra GETs when the page remounts within the stale window
        // (e.g. layout swaps, Strict Mode). Concurrent callers are also
        // deduped in api/rest/completed-forms.ts.
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function usePatchCompletedForm() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async ({
            userId,
            formStep,
            isCompleted = true,
            payload,
        }: {
            userId: number;
            formStep?: visaApplicationSteps | string;
            isCompleted?: boolean;
            /** When provided, sent as-is (e.g. { personal_info: true, purpose: true }). */
            payload?: Record<string, boolean>;
        }) => {
            const profileId = getActingProfileUserId() ?? userId;
            if (!profileId || profileId <= 0) {
                return {
                    userId: profileId,
                    body: null as Record<string, boolean> | null,
                    data: null,
                };
            }

            const body = payload ?? (formStep ? { [formStep]: isCompleted } : null);
            if (!body || Object.keys(body).length === 0) {
                return { userId: profileId, body: null, data: null };
            }

            const { data } = await api.visaApplication.updateCompletedForms(profileId, body);
            return { userId: profileId, body, data };
        },
        onSuccess: (result) => {
            if (!result?.userId || !result.body) return;

            const key = visaApplicationKeys.completedForms(result.userId);

            // Prefer cache merge over invalidate+refetch so we don't fire another GET.
            // If the API returns a full map, use it; otherwise merge the PATCH payload.
            queryClient.setQueryData<CompletedFormInterface | null>(key, (prev) => {
                const fromServer =
                    result.data && typeof result.data === 'object' && !Array.isArray(result.data)
                        ? (result.data as CompletedFormInterface)
                        : null;
                if (fromServer && Object.keys(fromServer).length > 0) {
                    return { ...(prev ?? {}), ...fromServer };
                }
                return { ...(prev ?? {}), ...result.body } as CompletedFormInterface;
            });
        },
        onError,
    });
}

/* ═══════════════════════════════════════════════════════
   GET hooks
   ═══════════════════════════════════════════════════════ */

export function usePersonalInfo() {
    const query = useQuery<PersonInfoForm>({
        queryKey: visaApplicationKeys.personalInfo,
        queryFn: async () => {
            const { data } = await api.visaApplication.getPersonalInfo();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useCitizenship() {
    const query = useQuery<CitizenshipInfo[]>({
        queryKey: visaApplicationKeys.citizenshipInfo,
        queryFn: async () => {
            const { data } = await api.visaApplication.getCitizenship();
            return data;
        },
        staleTime: STALE_1_HOUR,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function usePurpose() {
    const query = useQuery<PurposeInfo>({
        queryKey: visaApplicationKeys.purposeInfo,
        queryFn: async () => {
            const { data } = await api.visaApplication.getPurpose();
            return data;
        },
        staleTime: STALE_1_HOUR,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useAdditionalDetails() {
    const query = useQuery<AdditionalDetailsForm>({
        queryKey: visaApplicationKeys.additionalDetails,
        queryFn: async () => {
            const { data } = await api.visaApplication.getAdditionalDetails();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function usePreviousVisaDetails() {
    const query = useQuery<PreviouslyHeldVisa[]>({
        queryKey: visaApplicationKeys.previousVisaDetails,
        queryFn: async () => {
            const { data } = await api.visaApplication.getPreviousVisaDetails();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useSpouseInfo(enabled = true) {
    const { spouseUserId, isLoading: spouseIdLoading } = useSpouseUserId();
    const query = useQuery<SpouseInfo>({
        queryKey: [...visaApplicationKeys.spouseInfo, spouseUserId],
        queryFn: async () => {
            if (spouseUserId) {
                const [userRes, personalRes, citizenshipRes] = await Promise.all([
                    api.visaApplication.getProfileTable('User', spouseUserId),
                    api.visaApplication.getProfileTable('PersonalInfo', spouseUserId),
                    api.visaApplication.getProfileTable('Citizenship', spouseUserId),
                ]);
                return mergeSpouseUserTables(
                    userRes.data,
                    personalRes.data,
                    citizenshipRes.data
                ) as SpouseInfo;
            }
            const { data } = await api.visaApplication.getSpouseInfo();
            return data;
        },
        enabled: enabled && !spouseIdLoading,
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useFamilyMembers() {
    const query = useQuery<FamilyMember[]>({
        queryKey: visaApplicationKeys.familyMembers,
        queryFn: async () => {
            const { data } = await api.visaApplication.getFamilyMembers();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useChildren() {
    const query = useQuery<FamilyMember[]>({
        queryKey: visaApplicationKeys.children,
        queryFn: async () => {
            const { data } = await api.visaApplication.getChildren();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useLanguageProficiency() {
    const query = useQuery<LanguageProficiency[]>({
        queryKey: visaApplicationKeys.languageProficiency,
        queryFn: async () => {
            const { data } = await api.visaApplication.getLanguageProficiency();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useSpouseLanguageProficiency(enabled = true) {
    const { spouseUserId, isLoading: spouseIdLoading } = useSpouseUserId();
    const query = useQuery<LanguageProficiency[]>({
        queryKey: [...visaApplicationKeys.spouseLanguageProficiency, spouseUserId],
        queryFn: async () => {
            if (spouseUserId) {
                const { data } = await api.visaApplication.getProfileTable(
                    'Language',
                    spouseUserId
                );
                return data ?? [];
            }
            const { data } = await api.visaApplication.getSpouseLanguageProficiency();
            return data;
        },
        enabled: enabled && !spouseIdLoading,
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useEducation() {
    const query = useQuery<Education[]>({
        queryKey: visaApplicationKeys.education,
        queryFn: async () => {
            const { data } = await api.visaApplication.getEducation();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useSpouseEducation(enabled = true) {
    const { spouseUserId, isLoading: spouseIdLoading } = useSpouseUserId();
    const query = useQuery<Education[]>({
        queryKey: [...visaApplicationKeys.education, 'spouse', spouseUserId],
        queryFn: async () => {
            if (spouseUserId) {
                const { data } = await api.visaApplication.getProfileTable(
                    'EducationNew',
                    spouseUserId
                );
                return data ?? [];
            }
            const { data } = await api.visaApplication.getSpouseEducation();
            return data ?? [];
        },
        enabled: enabled && !spouseIdLoading,
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useWorkExperience() {
    const query = useQuery<WorkExperience[]>({
        queryKey: visaApplicationKeys.workExperience,
        queryFn: async () => {
            const { data } = await api.visaApplication.getWorkExperience();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useSpouseWorkExperience(enabled = true) {
    const { spouseUserId, isLoading: spouseIdLoading } = useSpouseUserId();
    const query = useQuery<PartnerWorkExperienceInterface[]>({
        queryKey: [...visaApplicationKeys.spouseWorkExperience, spouseUserId],
        queryFn: async () => {
            if (spouseUserId) {
                const { data } = await api.visaApplication.getProfileTable(
                    'WorkExperienceNew',
                    spouseUserId
                );
                return data ?? [];
            }
            const { data } = await api.visaApplication.getSpouseWorkExperience();
            return data;
        },
        enabled: enabled && !spouseIdLoading,
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useJobOffer() {
    const query = useQuery<JobOffer[]>({
        queryKey: visaApplicationKeys.jobOffer,
        queryFn: async () => {
            const { data } = await api.visaApplication.getJobOffer();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useRelativesAbroad() {
    const query = useQuery<RelativeAbroad[]>({
        queryKey: visaApplicationKeys.relativesAbroad,
        queryFn: async () => {
            const { data } = await api.visaApplication.getRelativesAbroad();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

export function useBusinessOwnership() {
    const query = useQuery<BusinessOwnership[]>({
        queryKey: visaApplicationKeys.businessOwnership,
        queryFn: async () => {
            const { data } = await api.visaApplication.getBusinessOwnership();
            return data;
        },
        staleTime: STALE_5_MIN,
    });
    useAlertOnQueryError(query.error);
    return query;
}

/* ═══════════════════════════════════════════════════════
   Tickets
   ═══════════════════════════════════════════════════════ */

export function useResolveApplicationTicket() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async ({
            id,
            status,
            applicationId,
        }: {
            id: number;
            status: string;
            applicationId?: number;
        }) => {
            const { data } = await api.visaApplication.resolveApplicationTicket(
                id,
                status,
                applicationId
            );
            return data;
        },
        onSuccess: (_data, variables) => {
            const data = _data as unknown;
            const appId = variables?.applicationId;
            if (!appId) return;

            if (Array.isArray(data)) {
                queryClient.setQueryData(
                    visaApplicationKeys.applicationTickets(appId),
                    data as TicketInterface[]
                );
                return;
            }

            if (typeof data === 'object' && data !== null && 'tickets' in data) {
                const d = data as { tickets: unknown };
                if (Array.isArray(d.tickets)) {
                    queryClient.setQueryData(
                        visaApplicationKeys.applicationTickets(appId),
                        d.tickets as TicketInterface[]
                    );
                }
                return;
            }

            if (typeof data === 'object' && data !== null && 'id' in data) {
                const typed = data as TicketInterface;
                queryClient.setQueryData<TicketInterface[] | undefined>(
                    visaApplicationKeys.applicationTickets(appId),
                    (old) => {
                        if (!old) return old;
                        const idx = old.findIndex((t) => t.id === typed.id);
                        if (idx >= 0) {
                            const copy = old.slice();
                            copy[idx] = { ...copy[idx], ...typed };
                            return copy;
                        }
                        return [typed, ...old];
                    }
                );
            }
        },
        onError,
    });
}

/* ═══════════════════════════════════════════════════════
   Save hooks
   ═══════════════════════════════════════════════════════ */

/**
 * Note: completed-forms PATCH is handled by the visa application container
 * after each step submit (supports multi-key payloads e.g. personal_info + purpose).
 * Save mutations only invalidate their own query caches.
 */

export function useSavePersonalInfo() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: PersonInfoForm) => {
            const { data } = await api.visaApplication.savePersonalInfo(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.personalInfo });
        },
        onError,
    });
}

export function useSavePurpose() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (purpose: string) => {
            const { data } = await api.visaApplication.savePurpose(purpose);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.purposeInfo });
        },
        onError,
    });
}

export function useSaveCitizenship() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (countries: string[]) => {
            const { data } = await api.visaApplication.saveCitizenship(countries);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.citizenshipInfo });
        },
        onError,
    });
}

export function useSaveAdditionalDetails() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: AdditionalDetailsForm) => {
            const { data } = await api.visaApplication.saveAdditionalDetails(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.additionalDetails });
        },
        onError,
    });
}

export function useSavePreviousVisaDetails() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: PreviouslyHeldVisa[]) => {
            const { data } = await api.visaApplication.savePreviousVisaDetails(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.previousVisaDetails });
        },
        onError,
    });
}

export function useSaveSpouseInfo() {
    const queryClient = useQueryClient();
    const { spouseUserId } = useSpouseUserId();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: SpouseInfo) => {
            if (spouseUserId) {
                const { user, personalInfo, citizenships } = splitSpouseProfilePayload(formData);
                const [userRes] = await Promise.all([
                    api.visaApplication.saveProfileTable('User', user, spouseUserId),
                    api.visaApplication.saveProfileTable(
                        'PersonalInfo',
                        personalInfo,
                        spouseUserId
                    ),
                    citizenships.length
                        ? api.visaApplication.saveProfileTable(
                              'Citizenship',
                              citizenships,
                              spouseUserId
                          )
                        : Promise.resolve(null),
                ]);
                return userRes.data;
            }
            const { data } = await api.visaApplication.saveSpouseInfo(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.spouseInfo });
        },
        onError,
    });
}

export function useSaveFamilyMembers() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: FamilyMember[]) => {
            const { data } = await api.visaApplication.saveFamilyMembers(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.familyMembers });
        },
        onError,
    });
}

export function useSaveChildren() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: FamilyMember[]) => {
            const { data } = await api.visaApplication.saveChildren(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.children });
        },
        onError,
    });
}

export function useSaveLanguageProficiency() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: LanguageProficiency[]) => {
            const { data } = await api.visaApplication.saveLanguageProficiency(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.languageProficiency });
        },
        onError,
    });
}

export function useSaveSpouseLanguageProficiency() {
    const queryClient = useQueryClient();
    const { spouseUserId } = useSpouseUserId();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: LanguageProficiency[]) => {
            if (spouseUserId) {
                const { data } = await api.visaApplication.saveProfileTable(
                    'Language',
                    formData,
                    spouseUserId
                );
                return data;
            }
            const { data } = await api.visaApplication.saveSpouseLanguageProficiency(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: visaApplicationKeys.spouseLanguageProficiency,
            });
        },
        onError,
    });
}

export function useSaveEducation() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: Education[]) => {
            const { data } = await api.visaApplication.saveEducation(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.education });
        },
        onError,
    });
}

export function useSaveSpouseEducation() {
    const queryClient = useQueryClient();
    const { spouseUserId } = useSpouseUserId();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: Partial<PartnerEducationInterface>[]) => {
            if (spouseUserId) {
                const { data } = await api.visaApplication.saveProfileTable(
                    'EducationNew',
                    formData,
                    spouseUserId
                );
                return data;
            }
            const { data } = await api.visaApplication.saveSpouseEducation(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.education });
        },
        onError,
    });
}

export function useSaveWorkExperience() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: WorkExperience[]) => {
            const { data } = await api.visaApplication.saveWorkExperience(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.workExperience });
        },
        onError,
    });
}

export function useSaveSpouseWorkExperience() {
    const queryClient = useQueryClient();
    const { spouseUserId } = useSpouseUserId();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: PartnerWorkExperienceInterface[]) => {
            if (spouseUserId) {
                const { data } = await api.visaApplication.saveProfileTable(
                    'WorkExperienceNew',
                    formData,
                    spouseUserId
                );
                return data;
            }
            const { data } = await api.visaApplication.saveSpouseWorkExperience(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.spouseWorkExperience });
        },
        onError,
    });
}

export function useSaveJobOffer() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: JobOffer[]) => {
            const { data } = await api.visaApplication.saveJobOffer(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.jobOffer });
        },
        onError,
    });
}

export function useSaveRelativesAbroad() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: RelativeAbroad[]) => {
            const { data } = await api.visaApplication.saveRelativesAbroad(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.relativesAbroad });
        },
        onError,
    });
}

export function useSaveBusinessOwnership() {
    const queryClient = useQueryClient();

    const onError = useMutationErrorAlert();

    return useMutation({
        mutationFn: async (formData: BusinessOwnership[]) => {
            const { data } = await api.visaApplication.saveBusinessOwnership(formData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: visaApplicationKeys.businessOwnership });
        },
        onError,
    });
}
